/**
 * CustomerId Pending Overlay – shown when a user (e.g. SSO login) has no
 * customerId assigned yet.
 *
 * Displays a full-page info card explaining that the account is active but
 * a customer number hasn't been assigned yet. The user should contact their
 * admin or wait for assignment.
 *
 * This overlay blocks interaction with pages that require a customerId
 * (cart, scanner, manual entry, etc.).
 */

import { authService } from '../../features/auth/auth.service';
import { ssoService } from '../../features/auth/sso.service';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let overlayElement: HTMLElement | null = null;

// ---------------------------------------------------------------------------
// SVG Icons
// ---------------------------------------------------------------------------

const waitIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Show the customerId-pending overlay on the given content container.
 * Replaces the container content entirely.
 * If already shown, does nothing.
 */
export function showCustomerIdPendingOverlay(contentContainer: HTMLElement): void {
  if (overlayElement) return;

  // Clear any existing content
  contentContainer.innerHTML = '';

  overlayElement = document.createElement('div');
  overlayElement.className = 'customerid-pending-overlay';
  overlayElement.setAttribute('role', 'dialog');
  overlayElement.setAttribute('aria-modal', 'true');
  overlayElement.setAttribute('aria-label', 'Kundennummer ausstehend');

  const user = authService.getUser();
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '';

  overlayElement.innerHTML = `
    <div class="customerid-pending-overlay__card">
      <div class="customerid-pending-overlay__icon">${waitIcon}</div>
      <h2 class="customerid-pending-overlay__title">Kundennummer ausstehend</h2>
      <p class="customerid-pending-overlay__desc">
        ${displayName ? `Hallo <strong>${escapeHtml(displayName)}</strong>, ` : ''}Ihr Konto wurde erfolgreich erstellt, aber es wurde Ihnen noch keine Kundennummer zugeordnet.
      </p>
      <p class="customerid-pending-overlay__desc">
        Bitte wenden Sie sich an Ihren Ansprechpartner bei Meesenburg, damit Ihnen eine Kundennummer zugewiesen wird. Anschließend können Sie alle Funktionen der App nutzen.
      </p>
      <div class="customerid-pending-overlay__actions">
        <button type="button" class="btn btn-primary" id="customerid-pending-retry-btn">
          Erneut prüfen
        </button>
        <button type="button" class="btn btn-secondary" id="customerid-pending-logout-btn">
          Abmelden
        </button>
        <button type="button" class="btn btn-secondary" id="customerid-pending-sso-logout-btn">
          Meesenburg ID-Abmelden
        </button>
      </div>
    </div>
  `;

  contentContainer.appendChild(overlayElement);

  // Bind retry button — reload the page to re-check token
  const retryBtn = overlayElement.querySelector<HTMLButtonElement>('#customerid-pending-retry-btn');
  retryBtn?.addEventListener('click', () => {
    window.location.reload();
  });

  // Bind logout button — local logout only
  const logoutBtn = overlayElement.querySelector<HTMLButtonElement>('#customerid-pending-logout-btn');
  logoutBtn?.addEventListener('click', async () => {
    logoutBtn.disabled = true;
    logoutBtn.textContent = 'Wird abgemeldet…';
    await authService.logoutAsync();
    window.location.reload();
  });

  // Bind SSO logout button — also terminate Keycloak session
  const ssoLogoutBtn = overlayElement.querySelector<HTMLButtonElement>('#customerid-pending-sso-logout-btn');
  ssoLogoutBtn?.addEventListener('click', async () => {
    ssoLogoutBtn.disabled = true;
    ssoLogoutBtn.textContent = 'Wird abgemeldet…';
    await authService.logoutAsync();
    window.location.href = ssoService.getLogoutUrl();
  });
}

/**
 * Hide and remove the customerId-pending overlay.
 */
export function hideCustomerIdPendingOverlay(): void {
  if (overlayElement) {
    overlayElement.remove();
    overlayElement = null;
  }
}

/**
 * Returns true if the overlay is currently visible.
 */
export function isCustomerIdPendingOverlayVisible(): boolean {
  return overlayElement !== null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}
